import React, { useCallback, useEffect, useState } from 'react';
import { toast } from '../../../util/toasts';
import usePrevious from '../../../hooks/use-previous';
import { debounce } from 'lodash';
import {
  StatusTransitionRequirement,
  StatusTransitionRequirementRequest,
  Step,
  TransitionableEntity,
} from '../../../clients/quipli-service/status-transition-requirement/status-transition-requirement';
import { useStatusTransitionRequirementApiV1 } from '../../../clients/quipli-service/status-transition-requirement/status-transition-requirement-api-v1';
import { SelectForm } from '../../../components/select-form/select-form';

function toRequest(requirement: StatusTransitionRequirement) {
  return { ...requirement, stepId: requirement.step.id };
}
export const OrderStatusRequirementConfig = () => {
  const [requirements, setRequirements] = useState<StatusTransitionRequirementRequest[]>([]);
  const prevRequirements = usePrevious(requirements);
  const [steps, setSteps] = useState<Step[]>();

  const { update, search, getSteps } = useStatusTransitionRequirementApiV1();

  useEffect(() => {
    search({ entity: TransitionableEntity.ORDER }).then((orderRequirements) => {
      setRequirements(orderRequirements.map(toRequest));
    });
    getSteps({ entity: TransitionableEntity.ORDER }).then(setSteps);
  }, []);

  useEffect(() => {
    if (prevRequirements && requirements) {
      debouncedDoUpdate(requirements);
    }
  }, [requirements]);

  const debouncedDoUpdate = useCallback(debounce(doUpdate, 500), []);

  function doUpdate(req: StatusTransitionRequirementRequest[]) {
    update(TransitionableEntity.ORDER, req).catch((e) => {
      console.error(e);
      toast('Failed to update status transition requirements', 'error');
    });
  }

  return (
    <div>
      {requirements && steps?.length && (
        <div className="flex flex-column gap-4">
          <div className="font-bold">On Create</div>
          <div className="flex items-center gap-4 pl-8">
            <div className="flex gap-2 items-center">
              <div className="flex gap-2 items-center">
                <label>Requirements</label>
                <SelectForm
                  menuShouldBlockScroll
                  menuPosition="fixed"
                  menuPortalTarget={document.body}
                  className="basis-1/2"
                  isMulti={true}
                  value={requirements
                    .filter((_) => _.fromStatus === 'Creation')
                    .map((_) => steps.find((step) => step.id == _.stepId)!)}
                  onChange={(e) => {
                    setRequirements((prev) => {
                      return [
                        ...prev.filter((_) => _.fromStatus !== 'Creation'),
                        ...e
                          .filter((_) => !!_)
                          .map((_) => ({
                            stepId: _.id,
                            fromStatus: 'Creation',
                            toStatus: 'Requested',
                            entity: TransitionableEntity.ORDER,
                          })),
                      ];
                    });
                  }}
                  options={steps.filter((_) => _.metadata.associated?.some((a) => a.fromStatuses.includes('Creation')))}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.id}
                  hideSelectedOptions={true}
                />
              </div>
            </div>
          </div>
          <div className="font-bold">On Approve</div>
          <div className="flex items-center gap-4 pl-8">
            <div className="flex gap-2 items-center">
              <div className="flex gap-2 items-center">
                <label>Requirements</label>
                <SelectForm
                  menuShouldBlockScroll
                  menuPosition="fixed"
                  menuPortalTarget={document.body}
                  className="basis-1/2"
                  isMulti={true}
                  value={requirements
                    .filter((_) => _.fromStatus === 'Requested')
                    .map((_) => steps.find((step) => step.id == _.stepId)!)}
                  onChange={(e) => {
                    setRequirements((prev) => {
                      return [
                        ...prev.filter((_) => _.fromStatus !== 'Requested'),
                        ...e
                          .filter((_) => !!_)
                          .map((_) => ({
                            stepId: _.id,
                            fromStatus: 'Requested',
                            toStatus: 'Approved',
                            entity: TransitionableEntity.ORDER,
                          })),
                      ];
                    });
                  }}
                  options={steps.filter((_) =>
                    _.metadata.associated?.some((a) => a.fromStatuses.includes('Requested')),
                  )}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.id}
                  hideSelectedOptions={true}
                />
              </div>
            </div>
          </div>
          <div className="font-bold">On Rent</div>
          <div className="flex items-center gap-4 pl-8">
            <div className="flex gap-2 items-center">
              <div className="flex gap-2 items-center">
                <label>Requirements</label>
                <SelectForm
                  menuShouldBlockScroll
                  menuPosition="fixed"
                  menuPortalTarget={document.body}
                  className="basis-1/2"
                  isMulti={true}
                  value={requirements
                    .filter((_) => _.fromStatus === 'Approved')
                    .map((_) => steps.find((step) => step.id == _.stepId)!)}
                  onChange={(e) => {
                    setRequirements((prev) => {
                      return [
                        ...prev.filter((_) => _.fromStatus !== 'Approved'),
                        ...e
                          .filter((_) => !!_)
                          .map((_) => ({
                            stepId: _.id,
                            fromStatus: 'Approved',
                            toStatus: 'OnRent',
                            entity: TransitionableEntity.ORDER,
                          })),
                      ];
                    });
                  }}
                  options={steps.filter((_) =>
                    _.metadata.associated.some(
                      (_) => _.fromStatuses.includes('Approved') && _.entity === TransitionableEntity.ORDER,
                    ),
                  )}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.id}
                  hideSelectedOptions={true}
                />
              </div>
            </div>
          </div>
          <div className="font-bold">Off Rent</div>
          <div className="flex items-center gap-4 pl-8">
            <div className="flex gap-2 items-center">
              <div className="flex gap-2 items-center">
                <label>Requirements</label>
                <SelectForm
                  menuShouldBlockScroll
                  menuPosition="fixed"
                  menuPortalTarget={document.body}
                  className="basis-1/2"
                  isMulti={true}
                  value={requirements
                    .filter((_) => _.fromStatus === 'OnRent')
                    .map((_) => steps.find((step) => step.id == _.stepId)!)}
                  onChange={(e) => {
                    setRequirements((prev) => {
                      return [
                        ...prev.filter((_) => _.fromStatus !== 'OnRent'),
                        ...e
                          .filter((_) => !!_)
                          .map((_) => ({
                            stepId: _.id,
                            fromStatus: 'OnRent',
                            toStatus: 'OffRent',
                            entity: TransitionableEntity.ORDER,
                          })),
                      ];
                    });
                  }}
                  options={steps.filter((_) =>
                    _.metadata.associated.some(
                      (_) => _.entity === TransitionableEntity.ORDER && _.fromStatuses.includes('OnRent'),
                    ),
                  )}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.id}
                  hideSelectedOptions={true}
                />
              </div>
            </div>
          </div>
          <div className="font-bold">Inventory Return</div>
          <div className="flex items-center gap-4 pl-8">
            <div className="flex gap-2 items-center">
              <div className="flex gap-2 items-center">
                <label>Requirements</label>
                <SelectForm
                  menuShouldBlockScroll
                  menuPosition="fixed"
                  menuPortalTarget={document.body}
                  className="basis-1/2"
                  isMulti={true}
                  value={requirements
                    .filter((_) => _.fromStatus === 'OffRent')
                    .map((_) => steps.find((step) => step.id == _.stepId)!)}
                  onChange={(e) => {
                    setRequirements((prev) => {
                      return [
                        ...prev.filter((_) => _.fromStatus !== 'OffRent'),
                        ...e
                          .filter((_) => !!_)
                          .map((_) => ({
                            stepId: _.id,
                            fromStatus: 'OffRent',
                            toStatus: 'InventoryReturn',
                            entity: TransitionableEntity.ORDER,
                          })),
                      ];
                    });
                  }}
                  options={steps.filter((_) =>
                    _.metadata.associated.some(
                      (_) => _.entity === TransitionableEntity.ORDER && _.fromStatuses.includes('OffRent'),
                    ),
                  )}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.id}
                  hideSelectedOptions={true}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
